import React, { useState } from 'react';
import {
  Card,
  Button,
  InputNumber,
  message,
  Space,
  Typography,
  Form,
  Progress,
} from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { saveAs } from 'file-saver';
import './index.less';

const { Title, Text } = Typography;

const BASE_URL = 'https://ds2.cn/spacex';

const R230Admin: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [form] = Form.useForm();

  const batchGenerateAndDownload = async (values: { quantity: number }) => {
    const { quantity } = values;
    if (!quantity || quantity <= 0) {
      message.warning('请输入有效的生成数量');
      return;
    }

    setLoading(true);
    setProgress({ current: 0, total: quantity });

    try {
      // 分批请求，每次最多 1000 条
      const BATCH_SIZE = 1000;
      const batches = Math.ceil(quantity / BATCH_SIZE);
      const allHashes: string[] = [];

      for (let i = 0; i < batches; i++) {
        const batchQty = Math.min(BATCH_SIZE, quantity - i * BATCH_SIZE);

        const response = await fetch('/api/v1/r230/admin/account-cards/generate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ quantity: batchQty }),
        });

        const data = await response.json();
        if (data.success) {
          allHashes.push(...data.data.hashes);
          setProgress({ current: allHashes.length, total: quantity });
        } else {
          message.error(data.message || '生成失败');
          setLoading(false);
          return;
        }
      }

      // 下载文本文件
      const content = allHashes.join('\r\n') + '\r\n';
      const BOM = new Uint8Array([0xEF, 0xBB, 0xBF]);
      const blob = new Blob([BOM, content], { type: 'text/plain;charset=utf-8' });
      const now = new Date();
      const mmdd = `${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
      const fileName = `R230_${quantity}_${mmdd}.txt`;
      saveAs(blob, fileName);

      message.success(`成功生成并下载 ${allHashes.length} 条账号卡链接`);
    } catch (error) {
      message.error('生成失败，请重试');
    } finally {
      setLoading(false);
      setProgress({ current: 0, total: 0 });
    }
  };

  return (
    <div className="r230-admin">
      <Card
        title={
          <Space>
            <Title level={4} style={{ margin: 0 }}>宇宙探索卡 - 账号卡管理</Title>
          </Space>
        }
      >
        <div style={{ maxWidth: 500 }}>
          <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            批量生成账号卡二维码链接（r230_account_card 表），生成后自动下载为 TXT 文件。
            <br />
            链接格式：{BASE_URL}/{'<hash>'}
          </Text>

          <Form
            form={form}
            layout="inline"
            onFinish={batchGenerateAndDownload}
          >
            <Form.Item
              name="quantity"
              label="生成数量"
              initialValue={100}
              rules={[{ required: true, message: '请输入生成数量' }]}
            >
              <InputNumber
                min={1}
                max={100000}
                style={{ width: 160 }}
                disabled={loading}
                placeholder="1-100000"
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                icon={<DownloadOutlined />}
              >
                生成并下载
              </Button>
            </Form.Item>
          </Form>

          {loading && progress.total > 0 && (
            <div style={{ marginTop: 16 }}>
              <Text>正在生成... {progress.current} / {progress.total}</Text>
              <Progress
                percent={Math.round((progress.current / progress.total) * 100)}
                status="active"
              />
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default R230Admin;
